/**
 * Admin AI command catalog API wrappers.
 *
 * Where: `pages/Commands`.
 * Uses: `apiClient` from `./client`.
 * Contract: GET `/commands/`; PATCH `/commands/:command_name/update/`; POST `/commands/:command_name/reset/`.
 * Behavior: list response is `{ output: { items } }`; update/reset return refreshed command row in `output`.
 */

import { apiClient } from "./client";
import type { AiCommand, AiCommandList } from "@/types/command";

export const commandApi = {
  list: async (params?: {
    search?: string;
    category?: string;
    page_token?: string;
    max_results?: number;
  }): Promise<AiCommandList> => {
    const { data } = await apiClient.get<{
      output: {
        items: AiCommand[];
        next_page_token: string | null;
        previous_page_token: string | null;
        total_results: number;
      };
    }>(
      "/commands/",
      { params },
    );
    return {
      items: data.output?.items ?? [],
      next_page_token: data.output?.next_page_token ?? null,
      previous_page_token: data.output?.previous_page_token ?? null,
      total_results: data.output?.total_results ?? 0,
    };
  },

  listCategories: async (): Promise<string[]> => {
    const { data } = await apiClient.get<{ output: string[] }>("/commands/categories/");
    return data.output ?? [];
  },

  update: async (
    commandName: string,
    payload: { command: string; description?: string; category?: string },
  ): Promise<AiCommand> => {
    const { data } = await apiClient.patch<{ output: AiCommand }>(
      `/commands/${commandName}/update/`,
      payload,
    );
    return data.output;
  },

  reset: async (commandName: string): Promise<AiCommand> => {
    const { data } = await apiClient.post<{ output: AiCommand }>(
      `/commands/${commandName}/reset/`,
      {},
    );
    return data.output;
  },
};

